
import React from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '@/components/utils/PageWrapper';
import AnimatedElement from '@/components/utils/AnimatedElement';
import { Target, ArrowLeft, ChevronRight } from 'lucide-react';
import AnimatedLink from '@/components/ui/animated-link';
import { motion } from 'framer-motion';

const AutonomousAgentsPage = () => {
  return (
    <PageWrapper>
      <main className="flex-grow pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="mb-6">
            <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to all services
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-2">
              <AnimatedElement animation="fadeIn">
                <div className="bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400 p-3 rounded-xl inline-block mb-4">
                  <Target className="h-10 w-10" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                  Autonomous <span className="text-primary">Agents</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-6">
                  Self-learning systems that operate independently to accomplish complex tasks.
                </p>
              </AnimatedElement>

              <AnimatedElement animation="slideUp" delay={0.2}>
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    Autonomous agents are intelligent systems that can perceive their environment, make decisions, and take actions without direct human intervention. Our autonomous agent solutions help businesses automate complex tasks, improve efficiency, and enhance decision-making.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We develop custom autonomous agent solutions tailored to your specific business needs. Our approach focuses on creating intelligent, adaptable systems that learn from experience and continuously improve over time.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Automate complex, multi-step tasks</li>
                    <li>Reduce human intervention in routine processes</li>
                    <li>Improve adaptability to changing conditions</li>
                    <li>Enhance decision-making with real-time insights</li>
                    <li>Scale operations without proportional increases in labor</li>
                    <li>Enable 24/7 operation of critical systems</li>
                  </ul>
                </div>
              </AnimatedElement>
            </div>

            <div className="md:col-span-1">
              <motion.div 
                className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <h3 className="text-xl font-semibold mb-4">Our Autonomous Agent Services</h3>
                <ul className="space-y-3">
                  {[
                    "Autonomous Decision Making",
                    "Multi-agent Systems",
                    "Self-healing Networks",
                    "Robotic Process Automation",
                    "Swarm Intelligence",
                    "Intelligent Assistants"
                  ].map((service, index) => (
                    <motion.li 
                      key={index}
                      className="flex items-start"
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>{service}</span>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-8">
                  <AnimatedLink to="/contact" fullWidth>
                    Contact Us
                  </AnimatedLink>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div 
            className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 p-8 rounded-2xl mb-16"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <h2 className="text-2xl font-semibold mb-4">Ready to implement autonomous systems?</h2>
            <p className="text-muted-foreground mb-6">
              Let's discuss how our autonomous agent solutions can help you automate complex tasks and enhance decision-making.
            </p>
            <AnimatedLink to="/contact">
              Get Started
            </AnimatedLink>
          </motion.div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default AutonomousAgentsPage;
